import type { ReactElement } from "react";
import { fullName, type ColourRoleName } from "odyssey-shared";
import type { NameMatch } from "./lookup.ts";
import { chatName } from "./names.ts";
import { ScopeIcon } from "./scope-icon.tsx";
import { pingPanelHeader, pingPanelRows, pingPanelSpoken, type PingPanel } from "./ping-panel.ts";
import type { ChatLine as StoredLine } from "./store.ts";
import { ChatBlock, ChatLine } from "./chat-line.tsx";
import { say } from "../../words.ts";
import { PageStaffBadge } from "../badge/badge.tsx";
import { badgeLook } from "../badge/look.ts";
import { badgeName } from "../badge/words.ts";

/**
 * The chat log's lines (ADR-068 decision 8 and its amendments), each in words that tell its kind apart, so colour is
 * never the only signal: a Local line as "Kaelen: text", an action as "* Kaelen text", a whisper as "From Kaelen:
 * text" or "To Kaelen: text", the page's information alone in the system colour, and a line that refuses or fails
 * behind the alert mark in the warning colour (R-0027). A name is the character's first name (its amendment "chat
 * lines show the first name"; `chatName`), the full name while two characters in the log share a first name, and
 * hovering or focusing it shows the full name in the panel's tooltip, which the panel's body reads from
 * `data-chat-full`. Another player's name is a button that starts a whisper to them by both names; it stays out of
 * the Tab order, since typing `/w` is the keyboard's path.
 * A line's text is isolated (`bdi`), so a right-to-left message cannot reorder the name before it. `/ping`'s panel
 * is a system line of four lines in the monospaced face, its measured lines a grid (ADR-068's amendment "`/ping` as
 * a network panel"). Every line with a scope starts with its scope's icon (ADR-068's amendment of 2026-09-27): a
 * Local line and an action Local's, a whisper Whisper's, a system line and `/ping`'s panel the system's; a line that
 * refuses or fails carries the alert mark alone, so it never reads as a scope. A line of Global, Trade or Help reads
 * "[Trade] Kaelen: text" in its channel's colour, its lead naming the channel in words (ADR-068's amendment
 * "Global, Trade and Help"); a Party line reads "[Party] Kaelen: text" in Party's colour behind Party's icon the same
 * way (ADR-101 decision 25), behind its channel's icon, Help's being original art the design does not draw (its
 * amendment "scope tabs, and Help's icon"). A death line is drawn in red behind a skull where a system line has the
 * system icon (ADR-068's amendment of 2026-10-03).
 */

/**
 * The colour role a death line and its skull are drawn in (ADR-068's amendment of 2026-10-03): #ff4a3a, legible on
 * the log where health red is not, and far enough from Duel's coral that Duel keeps it.
 */
export const DEATH_LINE_COLOUR: ColourRoleName = "infamy-criminal";

/**
 * The skull before a death line: the atlas's `ui/chat/skull_10`, original art in the death line's red inside the
 * scope icons' ring, laid out in the icon's slot. It names no scope, so it is no button, and it is hidden from
 * assistive technology, since the sentence says what happened.
 *
 * @returns The mark.
 */
export function DeathMark(): ReactElement {
  return <span className="ot-chat__death ot-ui-chat-skull_10" aria-hidden="true" data-chat="skull" />;
}

/**
 * The staff badge before a speaker's name (ADR-112 decision 4.5): the kit's badge in the line's own height, the caption's
 * 8, so a staff line is as tall as a player's; the glyph below UI scale 4, where 8 device pixels of art do not fit the
 * box, and the art from there. A leader's frame lies outside the box and moves no neighbour's line. Nothing for a line
 * that carries no badge.
 *
 * @param props The line's `badge`, 0 to 15 or absent.
 * @returns The badge, or null.
 */
function LineBadge({ badge }: { readonly badge: number | undefined }): ReactElement | null {
  const look = badgeLook(badge ?? 0);
  if (look === null) {
    return null;
  }
  return (
    <span className="ot-chat__badge" data-chat="badge" data-badge={look.badge} data-chat-full={badgeName(look)}>
      <PageStaffBadge badge={look.badge} box={8} />
    </span>
  );
}

/**
 * The alert mark before a line that refuses or fails: the atlas's `ui/chat/alert_7`, hidden from assistive
 * technology, since the sentence says what happened.
 *
 * @returns The mark.
 */
export function AlertMark(): ReactElement {
  return <span className="ot-chat__alert ot-ui-chat-alert_7" aria-hidden="true" data-chat="alert" />;
}

/**
 * Words a line keeps for assistive technology and for a copy of the log but does not show, the icon before it
 * showing the same (ADR-068's amendment "scope tabs, and Help's icon", item 9): a channel line's "[Trade] " and a
 * received whisper's "From ". Kept off the screen, not hidden, so a screen reader and a selection read them.
 *
 * @param props The `text`, its trailing space included.
 * @returns The words.
 */
function HiddenLead({ text }: { readonly text: string }): ReactElement {
  return (
    <span className="ot-chat__unseen" data-chat="lead">
      {text}
    </span>
  );
}

/**
 * A player's name in a line: a button starting a whisper to them, or plain text for the player's own name. Either
 * carries the full name for the panel's tooltip, and the button is named by it for assistive technology, so the
 * tooltip never holds the only copy.
 *
 * @param props The `name`, the first names the log shares (`shared`), whether it is the player's `own`, and what
 *   a click does (`onName`) and what a right click or long press does (`onMenu`, opening the player menu).
 * @returns The name.
 */
function PlayerName({ name, shared, own, onName, onMenu }: { readonly name: NameMatch; readonly shared: ReadonlySet<string>; readonly own: boolean; readonly onName: (name: NameMatch) => void; readonly onMenu?: ((name: NameMatch, x: number, y: number) => void) | undefined }): ReactElement {
  const shown = chatName(name, shared);
  const full = fullName(name.firstName, name.familyName);
  if (own) {
    return (
      <span className="ot-chat__name" data-chat-full={full} data-chat-card="">
        {shown}
      </span>
    );
  }
  return (
    <button type="button" className="ot-chat__name ot-chat__name--whisper" aria-label={full} tabIndex={-1} data-chat="name" data-chat-full={full} data-chat-card="" onClick={() => onName(name)} onContextMenu={onMenu === undefined ? undefined : (event) => onMenu(name, event.clientX, event.clientY)}>
      {shown}
    </button>
  );
}

/**
 * `/ping`'s panel: the header, then Network, Socket and Game response in three aligned columns, the label, the
 * average and the range, each line's meaning its tooltip; a line with no measure has its "not available" span
 * the average's and the range's columns, so it never widens the average's. A screen reader hears it as one message, the visually
 * hidden sentence, and never the grid, whose cells would read with no pause between them.
 *
 * @param props The `panel`'s numbers.
 * @returns The panel.
 */
function PingPanelLine({ panel }: { readonly panel: PingPanel }): ReactElement {
  return <ChatBlock kind="ping" tone="system" hook="ping" mono spoken={pingPanelSpoken(panel)} mark={<ScopeIcon scope="system" />} header={pingPanelHeader(panel)} rows={pingPanelRows(panel)} />;
}

/**
 * One line of the log.
 *
 * @param props The `line`, the player's own name (`self`, null before the welcome), the first names the log
 *   shares (`shared`, from `sharedFirstNames`), what a click on another player's name does (`onName`), and what a
 *   right click or long press on it does (`onMenu`, called with the name and the point in client CSS pixels; none
 *   opens no menu).
 * @returns The line's element.
 */
export function LogLine({ line, self, shared, onName, onMenu }: { readonly line: StoredLine; readonly self: NameMatch | null; readonly shared: ReadonlySet<string>; readonly onName: (name: NameMatch) => void; readonly onMenu?: ((name: NameMatch, x: number, y: number) => void) | undefined }): ReactElement {
  if (line.kind === "system") {
    return (
      <ChatLine kind="system" mark={<ScopeIcon scope="system" />}>
        {line.text}
      </ChatLine>
    );
  }
  if (line.kind === "death") {
    return (
      <ChatLine kind="death" mark={<DeathMark />}>
        {line.text}
      </ChatLine>
    );
  }
  if (line.kind === "ping") {
    return <PingPanelLine panel={line.panel} />;
  }
  if (line.kind === "error") {
    return (
      <ChatLine kind="error" mark={<AlertMark />}>
        {line.text}
      </ChatLine>
    );
  }
  const own = self !== null && self.firstName.toLowerCase() === line.firstName.toLowerCase() && self.familyName.toLowerCase() === line.familyName.toLowerCase();
  const name = <PlayerName name={line} shared={shared} own={own} onName={onName} onMenu={onMenu} />;
  if (line.kind === "channel") {
    return (
      <p className={`ot-chat__line ot-chat__line--${line.channel}`} data-chat-line={line.channel}>
        <ScopeIcon scope={line.channel} unnamed />
        <LineBadge badge={line.badge} />
        <span className="ot-chat__sender">
          <HiddenLead text={`${say("chat.channelLead", { channel: say(`chat.scopes.${line.channel}`) })} `} />
          {name}
        </span>
        {": "}
        <bdi className="ot-chat__text">{line.text}</bdi>
      </p>
    );
  }
  if (line.kind === "party") {
    return (
      <p className="ot-chat__line ot-chat__line--party" data-chat-line="party">
        <ScopeIcon scope="party" unnamed />
        <span className="ot-chat__sender">
          <HiddenLead text={`${say("chat.channelLead", { channel: say("chat.scopes.party") })} `} />
          {name}
        </span>
        {": "}
        <bdi className="ot-chat__text">{line.text}</bdi>
      </p>
    );
  }
  if (line.kind === "action") {
    return (
      <p className="ot-chat__line ot-chat__line--action" data-chat-line="action">
        <ScopeIcon scope="local" />
        <LineBadge badge={line.badge} />
        <span className="ot-chat__sender">* {name}</span> <bdi className="ot-chat__text">{line.text}</bdi>
      </p>
    );
  }
  if (line.kind === "whisper") {
    return (
      <p className="ot-chat__line ot-chat__line--whisper" data-chat-line={line.sent ? "whisper-sent" : "whisper"}>
        <ScopeIcon scope="whisper" unnamed />
        <LineBadge badge={line.badge} />
        <span className="ot-chat__sender">
          {line.sent ? `${say("chat.whisperLead.sent")} ` : <HiddenLead text={`${say("chat.whisperLead.received")} `} />}
          {name}
        </span>
        {": "}
        <bdi className="ot-chat__text">{line.text}</bdi>
      </p>
    );
  }
  return (
    <p className="ot-chat__line ot-chat__line--local" data-chat-line="local">
      <ScopeIcon scope="local" />
      <LineBadge badge={line.badge} />
      <span className="ot-chat__sender">{name}</span>
      {": "}
      <bdi className="ot-chat__text">{line.text}</bdi>
    </p>
  );
}
